import { apiClient } from '@/common/lib/api-client';

const basePath = (companyId) => `/v1/leads/companies/${companyId}/appointments`;

/**
 * GET /v1/leads/companies/{companyId}/appointments/
 */
export async function getAppointments(companyId, params = {}) {
  const { data } = await apiClient.get(`${basePath(companyId)}/`, { params });
  return data;
}

/**
 * Fetch all appointment pages sequentially.
 */
export async function getAllAppointments(companyId, params = {}) {
  const pageSize = params.page_size || 100;
  let page = 1;
  let results = [];
  let hasMore = true;

  while (hasMore) {
    const data = await getAppointments(companyId, {
      ...params,
      page,
      page_size: pageSize,
    });
    const batch = data?.results || (Array.isArray(data) ? data : []);
    results = results.concat(batch);

    if (Array.isArray(data)) {
      hasMore = false;
    } else if (data?.next) {
      page += 1;
    } else {
      const total = data?.count;
      hasMore =
        typeof total === 'number'
          ? results.length < total
          : batch.length >= pageSize;
      if (hasMore) page += 1;
      if (batch.length === 0) hasMore = false;
    }

    // Safety cap
    if (page > 50) break;
  }

  return results;
}

/**
 * GET /v1/leads/companies/{companyId}/appointments/{appointmentId}/
 */
export async function getAppointmentDetail({ companyId, appointmentId }) {
  const { data } = await apiClient.get(
    `${basePath(companyId)}/${appointmentId}/`,
  );
  return data;
}

/**
 * POST /v1/leads/companies/{companyId}/appointments/create/
 */
export async function createAppointment({ companyId, payload }) {
  const { data } = await apiClient.post(
    `${basePath(companyId)}/create/`,
    payload,
  );
  return data;
}

/**
 * POST /v1/leads/companies/{companyId}/appointments/{appointmentId}/send-meet-link/
 * No request body. Backend chooses session or template.
 */
export async function sendAppointmentMeetLink({ companyId, appointmentId }) {
  const { data } = await apiClient.post(
    `${basePath(companyId)}/${appointmentId}/send-meet-link/`,
  );
  return data;
}

/**
 * PUT /v1/leads/companies/{companyId}/appointments/{appointmentId}/update/
 */
export async function updateAppointment({ companyId, appointmentId, payload }) {
  const { data } = await apiClient.put(
    `${basePath(companyId)}/${appointmentId}/update/`,
    payload,
  );
  return data;
}

/**
 * DELETE /v1/leads/companies/{companyId}/appointments/{appointmentId}/delete/
 */
export async function deleteAppointment({ companyId, appointmentId }) {
  await apiClient.delete(`${basePath(companyId)}/${appointmentId}/delete/`);
}

/**
 * GET /v1/leads/companies/{companyId}/appointments/settings/
 */
export async function getAppointmentSettings(companyId) {
  const { data } = await apiClient.get(`${basePath(companyId)}/settings/`);
  return data;
}

/**
 * PATCH /v1/leads/companies/{companyId}/appointments/settings/
 */
export async function updateAppointmentSettings({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `${basePath(companyId)}/settings/`,
    payload,
  );
  return data;
}

/**
 * POST /v1/leads/companies/{companyId}/contacts/{contactId}/tasks/create/
 */
export async function createAppointmentTask({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/create/`,
    payload,
  );
  return data;
}

/**
 * POST /v1/leads/companies/{companyId}/contacts/{contactId}/tasks/{taskId}/complete/
 */
export async function completeAppointmentTask({
  companyId,
  contactId,
  taskId,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/${taskId}/complete/`,
  );
  return data;
}

/**
 * POST /v1/leads/companies/{companyId}/contacts/{contactId}/notes/create/
 */
export async function createAppointmentNote({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/create/`,
    payload,
  );
  return data;
}
